import type { TemplatizeReplacement, TemplatizeResult } from './templatizeTypes';

function countMatches(input: string, re: RegExp) {
  const matches = input.match(re);
  return matches?.length ?? 0;
}

function replaceAllWithCount(
  input: string,
  re: RegExp,
  replacement: string,
): { output: string; count: number } {
  const count = countMatches(input, re);
  if (count === 0) return { output: input, count: 0 };
  // Use the replacer-function form so `$$` in `replacement` is emitted
  // literally (Terraform escape `$${...}`). The string form treats `$$` as a
  // special pattern meaning a single `$`, which drops the escape.
  return { output: input.replace(re, () => replacement), count };
}

function normalizeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    const output: Record<string, unknown> = {};
    for (const [key, child] of entries) {
      output[key] = key === 'authorizerResultTtlInSeconds' ? 0 : normalizeValue(child);
    }
    return output;
  }
  return value;
}

function normalizeJsonText(input: string): { text: string; normalized: boolean } {
  if (!input.trim()) return { text: input, normalized: false };
  try {
    const parsed = JSON.parse(input);
    if (!parsed || typeof parsed !== 'object') {
      return { text: input, normalized: false };
    }

    if (!Array.isArray(parsed)) {
      delete (parsed as Record<string, unknown>).servers;
    }

    const normalized = normalizeValue(parsed);
    const text = JSON.stringify(normalized, null, 2);
    return { text, normalized: text !== input };
  } catch {
    return { text: input, normalized: false };
  }
}

/**
 * Best-effort port of `misc-scripts/api_gateway/openapi_templatize.sh`.
 *
 * This does NOT require valid JSON for templating; it operates on raw text.
 * If JSON parses, it will also normalize and drop top-level "servers".
 */
export function templatizeOpenApiDefinition(definitionText: string): TemplatizeResult {
  const rawText = definitionText ?? '';
  const replacements: TemplatizeReplacement[] = [];

  const regionRe = /\bus-(east|west)-[1-9]\b/g;
  const accountIdRe = /\b[0-9]{12}\b/g;
  const bucketRe = /\bphx-staged-manuals-[a-z]+\b/g;
  const domainRe = /\bclient-api\.[a-z0-9-]+\.app\.lexipol\.com\b/g;
  // Mirrors the sed rule: the whole URL — scheme included — collapses into
  // ${http_proxy_domain_name}, so the Terraform value carries the scheme. The
  // trailing guard stops a longer host (lexipol.community, ...com.example.net)
  // from being partly replaced; sed spells it as a consumed-and-restored char.
  const httpProxyUrlRe = /https:\/\/(?:[a-z0-9-]+\.)?lms\.lexipol\.com(?![a-z0-9.-])/g;

  const regions = [...new Set(rawText.match(regionRe) ?? [])];
  const accountIds = [...new Set(rawText.match(accountIdRe) ?? [])];
  const buckets = [...new Set(rawText.match(bucketRe) ?? [])];
  const domains = [
    ...new Set([...(rawText.match(domainRe) ?? []), ...(rawText.match(httpProxyUrlRe) ?? [])]),
  ];

  const normalized = normalizeJsonText(rawText);
  let text = normalized.text;

  const rules: Array<{ re: RegExp; to: string; fromHint: string }> = [
    { re: /arn:aws:/g, to: 'arn:$${AWS::Partition}:', fromHint: 'arn:aws:' },
    // Colon-delimited only, mirroring the sed rules `s/:us-(east|west)-[1-9]:/` and
    // `s/:[0-9]{12}:/`. There used to be additional \b-anchored rules here that rewrote a BARE
    // region or any bare 12-digit run — so "invoice 100000000001" became ${AWS::AccountId}. The
    // backend writes via the sed script, so those extra rules only ever made the UI preview lie
    // about what the PR would contain. Detection above still flags bare occurrences.
    { re: /:us-(east|west)-[1-9]:/g, to: ':$${AWS::Region}:', fromHint: 'us-<region>-<n>' },
    { re: /:[0-9]{12}:/g, to: ':$${AWS::AccountId}:', fromHint: '<12-digit-account-id>' },
    { re: /\$\{aws_region\}/g, to: '$${AWS::Region}', fromHint: '${aws_region}' },
    { re: /\$\{account_id\}/g, to: '$${AWS::AccountId}', fromHint: '${account_id}' },
    {
      re: /phx-staged-manuals-[a-z]+/g,
      to: '${phx_staged_manuals_bucket_name}',
      fromHint: 'phx-staged-manuals-<env>',
    },
    { re: domainRe, to: '${api_domain_name}', fromHint: 'client-api.<env>.app.lexipol.com' },
    {
      re: httpProxyUrlRe,
      to: '${http_proxy_domain_name}',
      fromHint: 'https://<env>.lms.lexipol.com',
    },
    {
      re: /https:\/\/client-api\.\$\{stageVariables\.env\}\.app\.lexipol\.com/g,
      to: 'https://${api_domain_name}',
      fromHint: 'https://client-api.${stageVariables.env}.app.lexipol.com',
    },
    {
      re: /"authorizerResultTtlInSeconds"\s*:(?!\s*0\s*[,}])[^,}]+/g,
      to: '"authorizerResultTtlInSeconds": 0',
      fromHint: 'authorizerResultTtlInSeconds:<any>',
    },
  ];

  for (const rule of rules) {
    const out = replaceAllWithCount(text, rule.re, rule.to);
    text = out.output;
    if (out.count > 0) {
      replacements.push({ from: rule.fromHint, to: rule.to, count: out.count });
    }
  }

  if (normalized.normalized && replacements.length === 0 && text !== rawText) {
    replacements.push({ from: 'formatting', to: 'standardized JSON', count: 1 });
  }

  return {
    outputText: text,
    replacements,
    hardcodedFindings: { regions, accountIds, buckets, domains },
  };
}
